// src/components/LeadsScreen.jsx
// Lista de Leads premium.
// Rotas:
// /leads      => lista
// /leads/new  => novo lead
// /leads/edit => edição via state
// Endpoint: GET /psy/lead?id=ID_USUARIO
// Token refresh via header x-new-token.
// UI isolada em .leads-* para não mexer em .dash-* global.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AlertCircle,
  ArrowRight,
  BarChart3,
  CheckCircle2,
  Clock3,
  Menu,
  Plus,
  RefreshCw,
  Search,
  Target,
  TrendingUp,
  Users,
  X,
} from "lucide-react";

import AppSidebar from "./AppSidebar";
import Logo from "../assets/logoClaro.webp";
import { useToast } from "../context/ToastContext";
import "../sass/pages/leads.scss";
import { API_BASE as CONFIG_API_BASE } from "../config/apiBase";

const API_BASE = (
  CONFIG_API_BASE ||
  process.env.REACT_APP_API_URL ||
  "https://api.psyassist.com.br"
).replace(/\/$/, "");

const AUTH_KEYS = {
  TOKEN: "psy_token",
  USER_ID: "psy_id",
  USER_NAME: "psy_usuario",
};

const STATUS_FILTERS = [
  { value: "todos", label: "Todos" },
  { value: "aberto", label: "Abertos" },
  { value: "fechado", label: "Fechados" },
  { value: "perdido", label: "Perdidos" },
];

function readStorage(keys = []) {
  for (const key of keys) {
    const value = sessionStorage.getItem(key) || localStorage.getItem(key);

    if (value && String(value).trim() !== "") {
      return value;
    }
  }

  return "";
}

function writeToken(token) {
  if (!token) return;

  try {
    sessionStorage.setItem(AUTH_KEYS.TOKEN, token);
    sessionStorage.setItem("token", token);
    localStorage.setItem(AUTH_KEYS.TOKEN, token);
  } catch {
    // storage indisponível
  }
}

function normalizeText(value = "") {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function safeNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function pad2(value) {
  return String(value).padStart(2, "0");
}

function formatBRDate(value) {
  if (!value) return "Sem data";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "Sem data";

  return `${pad2(date.getDate())}/${pad2(date.getMonth() + 1)}/${date.getFullYear()}`;
}

function getTimeBR(value) {
  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) return "--:--";

  return new Intl.DateTimeFormat("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function getFirstName(name = "") {
  const clean = String(name || "").trim();

  if (!clean) return "";

  return clean.split(/\s+/)[0];
}

function getInitials(name = "") {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!parts.length) return "LD";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();

  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function normalizeStatus(value = "") {
  const status = normalizeText(value);

  // IMPORTANTE:
  // "Não Fechado" também contém a palavra "fechado".
  // Por isso a validação de não fechado/perdido precisa vir ANTES de "fechado".
  if (
    status.includes("nao fechado") ||
    status.includes("nao-fechado") ||
    status.includes("naofechado") ||
    status.includes("perdido") ||
    status.includes("cancelado") ||
    status.includes("descartado")
  ) {
    return "perdido";
  }

  if (
    status === "fechado" ||
    status.includes("convertido") ||
    status.includes("ganho")
  ) {
    return "fechado";
  }

  return "aberto";
}

function statusLabel(value = "") {
  const status = normalizeStatus(value);

  if (status === "fechado") return "Fechado";
  if (status === "perdido") return "Perdido";
  return "Aberto";
}

function originLabel(value = "") {
  const clean = String(value || "").trim();
  return clean || "Não informado";
}

function contactLabel(lead = {}) {
  const phone = lead?.telefone || lead?.phone || lead?.whatsapp || lead?.celular;
  const email = lead?.email;

  if (phone && email) return `${phone} • ${email}`;
  if (phone) return String(phone);
  if (email) return String(email);

  return "Sem contato";
}

function leadName(lead = {}) {
  return String(lead?.nome || lead?.name || lead?.cliente || "Sem nome").trim();
}

function leadDate(lead = {}) {
  return lead?.dataCadastro || lead?.createdAt || lead?.data || lead?.updatedAt || null;
}

function buildSearchText(lead = {}) {
  return normalizeText(
    [
      leadName(lead),
      originLabel(lead?.origem),
      statusLabel(lead?.status),
      contactLabel(lead),
      formatBRDate(leadDate(lead)),
    ]
      .filter(Boolean)
      .join(" ")
  );
}

async function readApiError(response) {
  try {
    const contentType = response.headers.get("content-type") || "";

    if (contentType.includes("application/json")) {
      const payload = await response.json();
      return payload?.message || payload?.detail || payload?.error || JSON.stringify(payload);
    }

    const text = await response.text();
    return text || `HTTP ${response.status}`;
  } catch {
    return `HTTP ${response.status}`;
  }
}

function calcStats(leads = []) {
  const stats = {
    total: leads.length,
    aberto: 0,
    fechado: 0,
    perdido: 0,
    origemPrincipal: "Sem dados",
  };

  const origins = new Map();

  leads.forEach((lead) => {
    const status = normalizeStatus(lead?.status);
    stats[status] += 1;

    const origem = originLabel(lead?.origem);
    origins.set(origem, safeNumber(origins.get(origem)) + 1);
  });

  const topOrigin = Array.from(origins.entries()).sort((a, b) => b[1] - a[1])[0];

  if (topOrigin) {
    stats.origemPrincipal = `${topOrigin[0]} (${topOrigin[1]})`;
  }

  return stats;
}

function KpiCard({
  icon: Icon,
  label,
  value,
  description,
  tone = "blue",
}) {
  return (
    <article className={`leads-kpi leads-kpi--${tone}`}>
      <div className="leads-kpi__icon" aria-hidden="true">
        <Icon size={19} />
      </div>

      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{description}</small>
      </div>
    </article>
  );
}

function LeadSkeleton() {
  return (
    <div className="leads-skeleton" aria-hidden="true">
      <div className="leads-skeleton__hero" />
      <div className="leads-skeleton__kpis">
        <span />
        <span />
        <span />
        <span />
      </div>
      <div className="leads-skeleton__toolbar" />
      <div className="leads-skeleton__list">
        <span />
        <span />
        <span />
      </div>
    </div>
  );
}

function EmptyState({
  title,
  message,
  onPrimary,
  onSecondary,
  primaryText,
  secondaryText,
  isError = false,
}) {
  return (
    <section className={`leads-state ${isError ? "leads-state--error" : ""}`}>
      <div className="leads-state__icon" aria-hidden="true">
        {isError ? (
          <AlertCircle size={25} />
        ) : (
          <Target size={25} />
        )}
      </div>

      <strong>{title}</strong>
      <span>{message}</span>

      <div className="leads-state__actions">
        {secondaryText ? (
          <button
            className="leads-btn leads-btn--ghost-dark"
            type="button"
            onClick={onSecondary}
          >
            {secondaryText}
          </button>
        ) : null}

        {primaryText ? (
          <button
            className="leads-btn leads-btn--primary"
            type="button"
            onClick={onPrimary}
          >
            {isError ? <RefreshCw size={16} /> : <Plus size={16} />}
            {primaryText}
          </button>
        ) : null}
      </div>
    </section>
  );
}

export default function LeadsScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [leads, setLeads] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");

  const nomeUsuario = useMemo(() => readStorage([AUTH_KEYS.USER_NAME]) || "", []);
  const firstName = useMemo(() => getFirstName(nomeUsuario), [nomeUsuario]);

  const getValidToken = useCallback(async (signal) => {
    const currentToken = readStorage([AUTH_KEYS.TOKEN, "token"]);

    if (!currentToken) return "";

    try {
      const response = await fetch(`${API_BASE}/psy/lead?id=check`, {
        method: "GET",
        headers: { Authorization: `Bearer ${currentToken}` },
        signal,
      });

      const refreshed = response.headers.get("x-new-token");

      if (refreshed && refreshed.trim() !== "") {
        writeToken(refreshed);
        return refreshed;
      }

      return currentToken;
    } catch (error) {
      if (error?.name === "AbortError") throw error;

      return currentToken;
    }
  }, []);

  const fetchLeads = useCallback(
    async ({ signal, silent = false } = {}) => {
      const userId = readStorage([AUTH_KEYS.USER_ID, "id"]);

      if (!userId) {
        const message =
          "ID do usuário não encontrado. Faça login novamente.";

        setErrorMessage(message);
        setLeads([]);
        setLoading(false);
        setRefreshing(false);

        toast.error({
          title: "Sessão inválida",
          message,
          duration: 6500,
        });

        return;
      }

      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setErrorMessage("");

      try {
        const token = await getValidToken(signal);

        if (!token) {
          const message =
            "Token não encontrado. Faça login novamente.";

          setErrorMessage(message);
          setLeads([]);

          toast.error({
            title: "Sessão encerrada",
            message,
            duration: 6500,
          });

          return;
        }

        const response = await fetch(
          `${API_BASE}/psy/lead?id=${encodeURIComponent(userId)}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            signal,
          }
        );

        const refreshed = response.headers.get("x-new-token");

        if (refreshed && refreshed.trim() !== "") {
          writeToken(refreshed);
        }

        if (response.status === 401 || response.status === 403) {
          const message =
            "Sessão expirada. Faça login novamente.";

          setErrorMessage(message);
          setLeads([]);

          toast.error({
            title: "Sessão encerrada",
            message,
            duration: 6500,
          });

          return;
        }

        if (!response.ok) {
          const message = await readApiError(response);
          setErrorMessage(`Erro ao buscar leads (${response.status}). ${message}`);
          setLeads([]);
          return;
        }

        const data = await response.json().catch(() => []);
        setLeads(Array.isArray(data) ? data : []);
        setLastUpdatedAt(new Date());
      } catch (error) {
        if (error?.name === "AbortError") return;

        console.error("Erro ao buscar leads:", error);
        setErrorMessage("Erro de conexão com a API.");
        setLeads([]);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [getValidToken, toast]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchLeads({ signal: controller.signal });

    return () => controller.abort();
  }, [fetchLeads]);

  useEffect(() => {
    if (location.pathname !== "/leads") return undefined;

    const controller = new AbortController();
    fetchLeads({ signal: controller.signal, silent: true });

    return () => controller.abort();
  }, [location.pathname, fetchLeads]);

  const stats = useMemo(() => calcStats(leads), [leads]);

  const filteredLeads = useMemo(() => {
    const query = normalizeText(search);

    return leads.filter((lead) => {
      const matchesStatus =
        statusFilter === "todos" || normalizeStatus(lead?.status) === statusFilter;

      const matchesSearch = !query || buildSearchText(lead).includes(query);

      return matchesStatus && matchesSearch;
    });
  }, [leads, search, statusFilter]);

  const conversionRate = stats.total > 0 ? (stats.fechado / stats.total) * 100 : 0;

  const reload = () => fetchLeads({ silent: true });
  const onAdd = () => navigate("/leads/new");
  const onEdit = (lead) => navigate("/leads/edit", { state: { lead } });
  const goLogin = () => navigate("/login");
  const goReport = () => navigate("/reports/leads");

  return (
    <div className="leads-page">
      <div className="leads-bg" aria-hidden="true">
        <span className="leads-bg__orb leads-bg__orb--one" />
        <span className="leads-bg__orb leads-bg__orb--two" />
        <span className="leads-bg__orb leads-bg__orb--three" />
      </div>

      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <header className="leads-topbar">
        <button
          className="leads-icon-btn"
          type="button"
          onClick={() => setSidebarOpen(true)}
          aria-label="Abrir menu"
        >
          <Menu size={19} aria-hidden="true" />
        </button>

        <div className="leads-brand">
          <img src={Logo} alt="PsyAssist" className="leads-brand__logo" />
          <div className="leads-brand__text">
            <strong>Leads</strong>
            <span>Cadastro e acompanhamento • {nomeUsuario || "Usuário"}</span>
          </div>
        </div>

        <div className="leads-top-actions">
          <button
            className="leads-btn leads-btn--ghost leads-btn--desktop"
            type="button"
            onClick={reload}
            disabled={loading || refreshing}
          >
            <RefreshCw
              size={16}
              className={refreshing ? "leads-spin" : ""}
            />
            {refreshing ? "Atualizando..." : "Atualizar"}
          </button>

          <button
            className="leads-btn leads-btn--primary"
            type="button"
            onClick={onAdd}
          >
            <Plus size={16} />
            Novo lead
          </button>
        </div>
      </header>

      <main className="leads-main">
        {loading ? (
          <LeadSkeleton />
        ) : errorMessage ? (
          <EmptyState
            title="Não foi possível carregar os leads."
            message={errorMessage}
            onPrimary={String(errorMessage).includes("Sessão") ? goLogin : reload}
            onSecondary={() => navigate("/")}
            primaryText={String(errorMessage).includes("Sessão") ? "Ir para login" : "Tentar novamente"}
            secondaryText="Voltar"
            isError
          />
        ) : (
          <>
            <section className="leads-hero">
              <div className="leads-hero__content">
                <span className="leads-kicker">
                  <Target size={14} aria-hidden="true" />
                  Funil comercial
                </span>

                <h1>Leads e oportunidades</h1>

                <p>
                  {firstName ? `${firstName}, ` : ""}
                  acompanhe contatos, origem e evolução do funil até a
                  conversão em paciente, sem misturar cadastro operacional
                  com a análise detalhada dos indicadores.
                </p>

                <div className="leads-hero__actions">
                  <button
                    className="leads-btn leads-btn--hero-primary"
                    type="button"
                    onClick={onAdd}
                  >
                    <Plus size={16} />
                    Novo lead
                  </button>

                  <button
                    className="leads-btn leads-btn--hero-ghost"
                    type="button"
                    onClick={goReport}
                  >
                    <BarChart3 size={16} />
                    Relatório de leads
                  </button>
                </div>
              </div>

              <aside className="leads-hero-card">
                <div className="leads-hero-card__icon" aria-hidden="true">
                  <TrendingUp size={22} />
                </div>

                <div>
                  <small>Conversão atual</small>
                  <strong>{conversionRate.toFixed(1)}%</strong>
                  <span>
                    {stats.fechado} fechado(s) de {stats.total} lead(s)
                    {lastUpdatedAt
                      ? ` • Atualizado às ${getTimeBR(lastUpdatedAt)}`
                      : ""}
                  </span>
                </div>
              </aside>
            </section>

            <section className="leads-kpis">
              <KpiCard
                icon={Users}
                label="Total"
                value={stats.total}
                description="Leads cadastrados"
                tone="blue"
              />

              <KpiCard
                icon={CheckCircle2}
                label="Fechados"
                value={stats.fechado}
                description={`${conversionRate.toFixed(1)}% de conversão`}
                tone="green"
              />

              <KpiCard
                icon={Clock3}
                label="Abertos"
                value={stats.aberto}
                description="Aguardando acompanhamento"
                tone="amber"
              />

              <KpiCard
                icon={Target}
                label="Origem"
                value={stats.origemPrincipal}
                description="Canal com maior volume"
                tone="teal"
              />
            </section>

            <section className="leads-panel">
              <div className="leads-panel__head">
                <div>
                  <span>Lista</span>
                  <h2>Leads cadastrados</h2>
                  <p>Busque por nome, origem, status, contato ou data.</p>
                </div>

                <div className="leads-counter">
                  {filteredLeads.length} de {leads.length}
                </div>
              </div>

              <div className="leads-toolbar">
                <label className="leads-search">
                  <span aria-hidden="true"><Search size={16} /></span>
                  <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Buscar lead..."
                  />

                  {search ? (
                    <button type="button" onClick={() => setSearch("")} aria-label="Limpar busca">
                      <X size={14} />
                    </button>
                  ) : null}
                </label>

                <div className="leads-filter-group" aria-label="Filtro de leads">
                  {STATUS_FILTERS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={statusFilter === option.value ? "is-active" : ""}
                      onClick={() => setStatusFilter(option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {filteredLeads.length === 0 ? (
                <div className="leads-empty">
                  <strong>Nenhum lead encontrado.</strong>
                  <span>Adicione um novo lead ou ajuste os filtros atuais.</span>
                  <button
                    className="leads-btn leads-btn--primary"
                    type="button"
                    onClick={onAdd}
                  >
                    <Plus size={16} />
                    Adicionar lead
                  </button>
                </div>
              ) : (
                <div className="leads-list">
                  {filteredLeads.map((lead, index) => {
                    const nome = leadName(lead);
                    const origem = originLabel(lead?.origem);
                    const status = statusLabel(lead?.status);
                    const statusKey = normalizeStatus(lead?.status);
                    const date = formatBRDate(leadDate(lead));
                    const key = lead?._id || lead?.id || `${index}-${nome}`;

                    return (
                      <button
                        key={key}
                        className={`leads-card is-${statusKey}`}
                        type="button"
                        onClick={() => onEdit(lead)}
                        title="Editar lead"
                      >
                        <div className="leads-avatar" aria-hidden="true">
                          {getInitials(nome)}
                        </div>

                        <div className="leads-card__mid">
                          <div className="leads-title">{nome}</div>

                          <div className="leads-sub">
                            <span>Origem: {origem}</span>
                            <span className="dot">•</span>
                            <span>{contactLabel(lead)}</span>
                          </div>
                        </div>

                        <div className="leads-card__right">
                          <span className={`leads-tag is-${statusKey}`}>{status}</span>
                          <span className="leads-date">{date}</span>
                          <span className="leads-chev" aria-hidden="true">
                            <ArrowRight size={16} />
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}
